import type { ConnectedAccountsEmbed } from 'pejer-embeddables/src/Pejer/tables/ConnectedAccounts'
import type { InvoicesEmbed } from 'pejer-embeddables/src/Pejer/tables/Invoices'
import type { PaymentsEmbed } from 'pejer-embeddables/src/Pejer/tables/Payments'
import type { PayoutsEmbed } from 'pejer-embeddables/src/Pejer/tables/Payouts'
import type { RefundRequestEmbed } from 'pejer-embeddables/src/Pejer/tables/RefundRequests'
import type { PejerTableNames } from 'pejer/src/types/base'
import type { SearchQuery } from 'pejer/src/types/types'
import { overviewRouteDefinitions } from './Routes/Overview/settings'
import { templates } from './templates'

export enum Routes {
  OVERVIEW = '/',
  ACCOUNTS = '/accounts',
  CREATE_ACCOUNT = '/accounts/create',
  PAYMENTS = '/payments',
  PAYMENTS_REFUND = '/payments/{paymentReferenceId}/refund',
  INVOICES = '/invoices',
  INVOICES_REFUND = '/invoices/{paymentReferenceId}/refund',
  PAYOUTS = '/payouts',
  REFUND_REQUESTS = '/refunds/requests',
  EXTERNAL_ACCOUNT_ONBOARDING = '/external-account/onboarding',
}

export interface RouteSearchQuery {
  [Routes.ACCOUNTS]: SearchQuery<ConnectedAccountsEmbed.ListItem>
  [Routes.INVOICES]: SearchQuery<InvoicesEmbed.ListItem>
  [Routes.PAYOUTS]: SearchQuery<PayoutsEmbed.ListItem>
  [Routes.PAYMENTS]: SearchQuery<PaymentsEmbed.ListItem>
  [Routes.REFUND_REQUESTS]: SearchQuery<RefundRequestEmbed.ListItem>
}

export const RouteToTable = {
  [Routes.ACCOUNTS]: 'connectedAccounts',
  [Routes.PAYMENTS]: 'payments',
  [Routes.PAYOUTS]: 'payouts',
  [Routes.INVOICES]: 'invoices',
  [Routes.REFUND_REQUESTS]: 'refundRequests',
} as const satisfies {
  // This keyof is temporary, it should be replaced with the actual table names
  [key in keyof RouteSearchQuery]: PejerTableNames
}

export type RoutesConfig = {
  [Routes.OVERVIEW]: {
    subroute:
      | (typeof overviewRouteDefinitions)[number]['routeName']
      | `${(typeof overviewRouteDefinitions)[number]['routeName']}`
  }
  [Routes.CREATE_ACCOUNT]: never
  [Routes.INVOICES_REFUND]: never
  [Routes.PAYMENTS_REFUND]: never
  [Routes.ACCOUNTS]: TableRouteConfig<Routes.ACCOUNTS>
  [Routes.PAYMENTS]: TableRouteConfig<Routes.PAYMENTS>
  [Routes.PAYOUTS]: TableRouteConfig<Routes.PAYOUTS>
  [Routes.INVOICES]: TableRouteConfig<Routes.INVOICES>
  [Routes.REFUND_REQUESTS]: TableRouteConfig<Routes.REFUND_REQUESTS>
  [Routes.EXTERNAL_ACCOUNT_ONBOARDING]: never
}

/**
 * Route events
 * These are the events that are allowed to be sent to the routes. These can be used to update filters, pagination etc.
 */
export type RouteEvents = {
  [Routes.OVERVIEW]: never
  [Routes.CREATE_ACCOUNT]: never
  [Routes.INVOICES_REFUND]: never
  [Routes.PAYMENTS_REFUND]: never
  [Routes.ACCOUNTS]: TableRouteEvent<Routes.ACCOUNTS>
  [Routes.PAYMENTS]: TableRouteEvent<Routes.PAYMENTS>
  [Routes.PAYOUTS]: TableRouteEvent<Routes.PAYOUTS>
  [Routes.INVOICES]: TableRouteEvent<Routes.INVOICES>
  [Routes.REFUND_REQUESTS]: TableRouteEvent<Routes.REFUND_REQUESTS>
  [Routes.EXTERNAL_ACCOUNT_ONBOARDING]: never
}

// export type RoutesTemplates = {
//   [Routes.OVERVIEW]: never
//   [Routes.INVOICES]: 'unpaid' | 'partially paid'
//   [Routes.PAYMENTS]: 'paid'
//   [Routes.CREATE_ACCOUNT]: never
//   [Routes.INVOICES_REFUND]: never
//   [Routes.ACCOUNTS]: never
//   [Routes.PAYOUTS]: never
// }
export type RoutesTemplates<T extends Routes> = T extends keyof typeof templates
  ? keyof (typeof templates)[T]
  : never

export type RoutesTemplateStructure<T extends Routes> = Pick<
  RouteDefinition<T>,
  'options' | 'displayText'
>

type RoutesParams = {
  [Routes.INVOICES_REFUND]: { paymentReferenceId: string }
  [Routes.PAYMENTS_REFUND]: { paymentReferenceId: string }
}

export type EmbeddableNavbarItem = LaxRouteDefinition
export type EmbeddableNavbar = EmbeddableNavbarItem[] | boolean
export type EmbeddableNavigation<T extends Routes> = LaxNavigationType<T>

/// Helpers
/**
 * Route object definition
 * This is a definition of a route object. It contains everything needed to define a navigation source.
 * Meaning this will be used in the displayNavigation settings to define the routes that should be displayed.
 */
export type RouteDefinition<T extends Routes = Routes> = PathRouteObjectBase<T> &
  Partial<NamedRouteObjectBase> &
  RouteOptions<RoutesConfig[T]> & {
    displayText?: string
    // template?: string
  }

export type RoutesDefinitions = {
  [K in keyof RoutesConfig]: RouteDefinition<K>
}[keyof RoutesConfig]

export type LaxRouteDefinition<R extends Routes = Routes> =
  | RouteDefinition<R>
  | Exclude<Routes, RoutesWithParams>

export type LaxRoutesDefinitions = {
  [K in keyof RoutesConfig]: LaxRouteDefinition<K>
}[keyof RoutesConfig]

/**
 * Route object
 * This is the actual route object that will be used to navigate to a route.
 */
export type NavigationObject<T extends Routes = Routes> =
  | (PathRouteObjectBase<T> & { routeName?: never } & RouteOptions<RoutesConfig[T]>)
  | (NamedRouteObjectBase & { path?: never } & RouteOptions<RouteEvents[T]>)

export type NavigationsObject = {
  [K in keyof RoutesConfig]: NavigationObject<K>
}[keyof RoutesConfig]

/**
 * Navigation object
 * This is the object that will be used to define the navigation in the settings.
 * It can be a route object or a routeName.
 * @type string - The route name
 */
export type LaxNavigationType<T extends Routes> =
  | string
  | Exclude<Routes, RoutesWithParams>
  | NavigationObject<T>

export type LaxNavigationTypes = string | Exclude<Routes, RoutesWithParams> | NavigationsObject

export type RouteFromRouteName<Route, T extends string> = Extract<Route, { routeName: T }>

export type RoutesWithParams = {
  [K in keyof RoutesParams]: K
}[keyof RoutesParams]

/**
 * Base route types
 */
export type PathRouteObjectBase<T extends Routes> = {
  path: T
  template?: RoutesTemplates<T>
} & (T extends keyof RoutesParams
  ? {
      params: RoutesParams[T]
    }
  : {})

export type NamedRouteObjectBase = {
  routeName: string
}

export type TableRouteConfig<T extends Routes> = {
  table?: {
    staticSearch?: T extends keyof RouteSearchQuery ? RouteSearchQuery[T] : never
    /**
     * Table search options
     * @param useSearch
     *  - If true, the table will have a search input
     *  - If false, the table will not have any search related inputs
     *  - If 'custom', the table will have still have the search icons in the table header, but no input.
     *    This is useful if you want to implement your own search functionality.
     *    Use the
     *    TODO: Add event here to listen for search events.
     */
    useSearch?: true | false | 'custom'
    /**
     * Table pagination options
     * @param usePagination
     *  - If true, the pagination will be enabled and events will be sent
     *  - If false, pagination will be disabled, but events will NOT still be sent
     *  - If 'custom', pagination will be disabled, but events will still be sent
     */
    usePagination?: true | false | 'custom'
  }
} & TableRouteEvent<T>

type RouteFilterSearch<T extends Routes> = T extends keyof RouteSearchQuery
  ? RouteSearchQuery[T]
  : never

export type TableRouteEvent<T extends Routes> = {
  table?: {
    /**
     * Table search
     * @param search The search query
     */
    search?: RouteFilterSearch<T>

    /**
     * Table pagination options
     * @param size Number of items per page
     * @param page The page number
     */
    pagination?: {
      size?: number
      page?: number
    }

    /**
     * Table sorting options
     * @param sortBy The column to sort by
     * @param sorter The sorting direction
     */
    sorting?: {
      sortBy: keyof RouteFilterSearch<T>
      sorter: 'Ascending' | 'Descending'
    }

    /**
     * Table columns visibility
     * @param columnsVisible The columns to show
     *  - If not provided, a set of default columns will be shown
     */
    columnsVisible?: (keyof RouteFilterSearch<T> | 'actions')[]
  }
}

export type RouteParams<T extends Object> = {
  params: T
}

export type RouteOptions<T> = {
  options?: T
}
